import { useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { BookOpen, Sparkles } from "lucide-react";
import { CTA, PRICE, CORE_VALUE } from "../config/brand";
import { PurchaseButton } from "../components/PurchaseButton";
import { LineReveal, Eyebrow } from "../components/Reveal";
import { scrollToId } from "../components/Navbar";
import { Tilt, usePointerParallax, hoverOnly } from "../components/Motion";

const CoreCard = ({ icon: Icon, title, text, delay, testId, active, dimmed, onActivate, onDeactivate, target }) => (
  <motion.button
    type="button"
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay }}
    data-testid={testId}
    {...hoverOnly({ onMouseEnter: onActivate, onMouseLeave: onDeactivate })}
    onFocus={onActivate}
    onBlur={onDeactivate}
    onClick={() => scrollToId(target)}
    className={`flex flex-1 items-start gap-3.5 rounded-2xl border bg-warmwhite p-4 text-left shadow-[0_8px_24px_rgba(90,48,73,0.05)] transition-[opacity,transform,border-color,box-shadow] duration-300 ${
      dimmed ? "scale-[0.985] border-mauve/30 opacity-55" : "border-mauve/40"
    } ${active ? "-translate-y-1 border-rose/60 shadow-[0_18px_44px_rgba(90,48,73,0.14)]" : ""}`}
  >
    <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors duration-300 ${active ? "bg-plum text-ivory" : "bg-blushcream text-plum"}`}>
      <Icon size={17} strokeWidth={1.5} />
    </span>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose">{title}</p>
      <p className="mt-1 text-[13px] leading-snug text-plumcharcoal/70">{text}</p>
    </div>
  </motion.button>
);

const Mockup = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 36]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -26]);
  const parallax = usePointerParallax();
  const bgLayer = parallax.useLayer(10);
  const hustleLayer = parallax.useLayer(5);
  const plannerLayer = parallax.useLayer(2);
  const badgeLayer = parallax.useLayer(9);

  return (
    <div
      ref={ref}
      className="relative mx-auto w-full max-w-[520px] pb-10"
      data-testid="hero-visual"
      onMouseMove={parallax.onMouseMove}
      onMouseLeave={parallax.onMouseLeave}
    >
      <motion.div aria-hidden="true" style={{ x: bgLayer.x, y: bgLayer.y }} className="absolute -inset-5 rounded-[2.2rem] bg-blushcream/80" />

      <motion.div style={{ y: yMain }} className="relative">
        <motion.div style={{ x: hustleLayer.x, y: hustleLayer.y }}>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 40, rotate: 2 }}
            animate={{ opacity: 1, y: 0, rotate: 1.5 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative ml-auto w-[62%]"
          >
            <Tilt max={4} className="group overflow-hidden rounded-2xl border border-mauve/30 bg-warmwhite shadow-[0_30px_70px_rgba(90,48,73,0.2)] transition-shadow duration-300 hover:shadow-[0_36px_80px_rgba(201,130,150,0.35)]">
              <img
                src="/assets/products/hustle.jpg"
                alt="AI Side Hustle Guide — How to Build a Profitable AI Side Hustle That Fits Your Life"
                className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                width={840}
                height={1087}
                fetchPriority="high"
              />
              <span className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-gold/0 via-blush/0 to-blush/25 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            </Tilt>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div style={{ y: yCard }} className="absolute left-0 top-8 w-[52%]">
        <motion.div style={{ x: plannerLayer.x, y: plannerLayer.y }}>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 44, rotate: -4 }}
            animate={{ opacity: 1, y: 0, rotate: -2.5 }}
            transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <Tilt max={3} className="group overflow-hidden rounded-2xl border border-mauve/30 bg-warmwhite shadow-[0_34px_80px_rgba(90,48,73,0.28)] transition-shadow duration-300 hover:shadow-[0_40px_90px_rgba(90,48,73,0.36)]">
              <img
                src="/assets/products/planner.jpg"
                alt="The Balanced Working Mom Planner cover"
                className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                width={840}
                height={1087}
                fetchPriority="high"
              />
              <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-plum/0 to-ivory/20 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            </Tilt>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div style={{ x: badgeLayer.x, y: badgeLayer.y }} className="absolute -bottom-2 right-2">
        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="rounded-2xl bg-plum px-5 py-3.5 shadow-[0_16px_40px_rgba(90,48,73,0.35)]"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-blush">9 digital resources</p>
          <p className="font-serif text-xl italic text-ivory">
            <span className="not-italic line-through opacity-60">{CORE_VALUE}</span>{" "}
            <span className="text-gold">{PRICE} today</span>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const [activeCard, setActiveCard] = useState(null);

  return (
    <section
      id="hero-section"
      className="relative overflow-hidden pt-[68px]"
      aria-label="Introduction"
      data-testid="hero-section-wrap"
    >
      <div className="grain relative mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-12 px-5 pb-14 pt-10 sm:px-8 lg:min-h-[calc(100vh-68px)] lg:grid-cols-12 lg:gap-8 lg:pb-8 lg:pt-4">
        <div className="lg:col-span-6 lg:pr-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <Eyebrow data-testid="hero-eyebrow">For the woman building a life — not just managing one</Eyebrow>
          </motion.div>

          <LineReveal
            lines={["Make room for the life", "you want—without dropping", "the life you already have."]}
            delay={0.2}
            className="mt-5 font-serif text-[2.4rem] font-medium leading-[1.06] tracking-tight text-plum sm:text-[3rem] lg:text-[2.9rem] xl:text-[3.15rem]"
          />

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-plumcharcoal/75 sm:text-lg"
            data-testid="hero-subheadline"
          >
            BloomParentify brings practical tools for working motherhood,
            everyday organization and AI-powered side-income exploration into one
            complete digital system.
          </motion.p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-testid="hero-core-solutions">
            <CoreCard
              icon={BookOpen}
              title="Core 01 · Balanced Working Mom Planner"
              text="Organize work, family, routines, priorities and your time—without holding everything in your head."
              delay={0.75}
              testId="hero-core-planner"
              target="life-system"
              active={activeCard === 0}
              dimmed={activeCard === 1}
              onActivate={() => setActiveCard(0)}
              onDeactivate={() => setActiveCard(null)}
            />
            <CoreCard
              icon={Sparkles}
              title="Core 02 · AI Side Hustle Guide"
              text="Explore practical ways to use AI to build an income opportunity around the life you already have."
              delay={0.85}
              testId="hero-core-hustle"
              target="ai-side-hustle"
              active={activeCard === 1}
              dimmed={activeCard === 0}
              onActivate={() => setActiveCard(1)}
              onDeactivate={() => setActiveCard(null)}
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-4"
          >
            <PurchaseButton event="hero_cta_click" testId="hero-get-system-button">
              {CTA.primary}
            </PurchaseButton>
            <div>
              <p className="font-serif text-2xl text-plum" data-testid="hero-price">
                <span className="mr-2 text-lg text-plumcharcoal/45 line-through">{CORE_VALUE}</span>
                {PRICE} <span className="text-base italic text-rose">today</span>
              </p>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-plumcharcoal/50">
                {CTA.microcopy}
              </p>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-6">
          <Mockup />
        </div>
      </div>
    </section>
  );
};
